/**
 * Print-safety placement rules, shared by the compose editor, the AI
 * layout endpoint, and the export preflight so all three enforce the
 * SAME geometry.
 *
 * The exported PDF is exactly trim size (no bleed), so safety comes from
 * placement:
 *   • TEXT must stay inside a safe area — SAFE_MARGIN_MM from every cut
 *     edge and from the gutter fold — because the cut can shift up to
 *     ±1.5 mm and the binding swallows the fold band.
 *   • IMAGES may sit at (or past) the cut line for full-bleed art. The
 *     hazard is the dead zone: a hard image edge that stops just short of
 *     the cut line prints as a thin white strip or a visibly uneven
 *     margin, so edges inside (0, EDGE_DEAD_ZONE_MM) get a warning —
 *     never an automatic correction.
 *
 * All checks work on the element percent coordinate system (x/w in % of
 * canvas width, y/h in % of canvas height; interior spreads are two
 * pages wide).
 */
import { MM_PER_INCH, SPREAD_DESIGN_W, formatPageInches } from './constants';
import type {
  FormatPreset,
  SpreadElement,
  SpreadKind,
  SpreadTextElement,
} from './types';

/** Text keep-out distance from every cut edge and the gutter fold (mm).
 *  Print-house safe areas run 3–4 mm; +1 mm because the file has no bleed,
 *  so an over-cut eats into the page itself. */
export const SAFE_MARGIN_MM = 5;

/** A hard image edge closer to the cut line than this — without touching
 *  it — risks printing as a white sliver after trimming (mm). */
export const EDGE_DEAD_ZONE_MM = 5;

/** Comparison slack so float drift never flags a clamped element (%). */
const EPSILON_PCT = 0.05;

/** A physical mm as percentages of each canvas axis for a format/kind. */
export function mmToCanvasPct(
  mm: number,
  formatPreset: FormatPreset | null | undefined,
  kind: SpreadKind,
): { xPct: number; yPct: number } {
  const { w, h } = formatPageInches(formatPreset);
  const canvasW = kind === 'spread' ? w * 2 : w;
  const inches = mm / MM_PER_INCH;
  return { xPct: (inches / canvasW) * 100, yPct: (inches / h) * 100 };
}

/**
 * The extra outward extent a text element's visuals reach past its box,
 * in canvas percent. Only the 'panel' readability effect grows the
 * footprint (the panel expands outward by its pad); halo/shadow hug the
 * glyphs. The pad is defined in spread design units of the canvas WIDTH
 * (matching the editor's cqw-based inset), so the vertical percent
 * scales by the canvas aspect.
 */
export function textOutsetPct(
  el: Pick<SpreadTextElement, 'effect'>,
  formatPreset: FormatPreset | null | undefined,
  kind: SpreadKind,
  panelPadDesign: number,
): { xPct: number; yPct: number } {
  if ((el.effect ?? 'none') !== 'panel') return { xPct: 0, yPct: 0 };
  const { w, h } = formatPageInches(formatPreset);
  const canvasW = kind === 'spread' ? w * 2 : w;
  const xPct = (panelPadDesign / SPREAD_DESIGN_W) * 100;
  return { xPct, yPct: xPct * (canvasW / h) };
}

export interface BoxPct {
  x: number;
  y: number;
  w: number;
  h: number;
}

const clamp = (v: number, lo: number, hi: number) =>
  Math.max(lo, Math.min(hi, v));

/**
 * Clamp a text box into the safe area: inside SAFE_MARGIN_MM of every cut
 * edge and — on interior spreads — fully on one side of the gutter's
 * keep-out band (a text box must never straddle the fold). Boxes wider or
 * taller than the allowed region shrink to fit. `outset` inflates the
 * box for visuals that extend past it (the panel effect), so the PANEL
 * stays inside the safe area, not just the glyph box.
 */
export function clampTextBoxToSafeArea(
  box: BoxPct,
  formatPreset: FormatPreset | null | undefined,
  kind: SpreadKind,
  outset: { xPct: number; yPct: number } = { xPct: 0, yPct: 0 },
): BoxPct {
  const margin = mmToCanvasPct(SAFE_MARGIN_MM, formatPreset, kind);
  const minX = margin.xPct + outset.xPct;
  const maxX = 100 - margin.xPct - outset.xPct;
  const minY = margin.yPct + outset.yPct;
  const maxY = 100 - margin.yPct - outset.yPct;

  let w = Math.min(box.w, maxX - minX);
  const h = Math.min(box.h, maxY - minY);
  let x = clamp(box.x, minX, maxX - w);
  const y = clamp(box.y, minY, maxY - h);

  if (kind === 'spread') {
    // The fold band no text may enter: the safe margin on each side of
    // the center line (plus the panel outset when present).
    const bandL = 50 - minX;
    const bandR = 50 + minX;
    if (x < bandR && x + w > bandL) {
      // Resolve to whichever page holds more of the box.
      const center = x + w / 2;
      if (center <= 50) {
        w = Math.min(w, bandL - minX);
        x = clamp(x, minX, bandL - w);
      } else {
        w = Math.min(w, maxX - bandR);
        x = clamp(x, bandR, maxX - w);
      }
    }
  }

  return { x, y, w, h };
}

/** Warning kinds surfaced by the editor icons and the export preflight. */
export type PlacementWarningKind =
  | 'text-unsafe' // text (or its panel) inside the trim/gutter safe margin
  | 'image-dead-zone'; // hard image edge just short of the cut line

export type CanvasEdge = 'left' | 'right' | 'top' | 'bottom';

export interface PlacementWarning {
  kind: PlacementWarningKind;
  /** Cut edges involved (dead-zone warnings only). */
  edges?: CanvasEdge[];
  /** True when the text violation is the gutter band, not an outer edge. */
  gutter?: boolean;
}

/**
 * Check one element's placement. Returns null when the placement is
 * print-safe. Purely informational — callers surface it as a warning and
 * never move the element or block an export.
 */
export function checkElementPlacement(
  el: SpreadElement,
  formatPreset: FormatPreset | null | undefined,
  kind: SpreadKind,
  panelPadDesign: number,
): PlacementWarning | null {
  if (el.type === 'text') {
    if (!el.text.trim()) return null;
    const outset = textOutsetPct(el, formatPreset, kind, panelPadDesign);
    const safe = clampTextBoxToSafeArea(el, formatPreset, kind, outset);
    const moved =
      Math.abs(safe.x - el.x) > EPSILON_PCT ||
      Math.abs(safe.y - el.y) > EPSILON_PCT ||
      Math.abs(safe.w - el.w) > EPSILON_PCT ||
      Math.abs(safe.h - el.h) > EPSILON_PCT;
    if (!moved) return null;
    const margin = mmToCanvasPct(SAFE_MARGIN_MM, formatPreset, kind);
    const bandHalf = margin.xPct + outset.xPct;
    const gutter =
      kind === 'spread' && el.x < 50 + bandHalf && el.x + el.w > 50 - bandHalf;
    return { kind: 'text-unsafe', gutter };
  }

  // Images: only hard rectangular silhouettes produce the white-sliver
  // hazard. Soft edges (feather/vignette/torn/brush/scallop) fade into
  // the page and cutout shapes read as deliberate spot art.
  const edge = el.edge ?? 'sharp';
  if (el.shape || (edge !== 'sharp' && edge !== 'rounded')) return null;

  const dead = mmToCanvasPct(EDGE_DEAD_ZONE_MM, formatPreset, kind);
  const gaps: Array<[CanvasEdge, number, number]> = [
    ['left', el.x, dead.xPct],
    ['right', 100 - (el.x + el.w), dead.xPct],
    ['top', el.y, dead.yPct],
    ['bottom', 100 - (el.y + el.h), dead.yPct],
  ];
  const edges = gaps
    .filter(([, gap, zone]) => gap > EPSILON_PCT && gap < zone)
    .map(([name]) => name);
  return edges.length > 0 ? { kind: 'image-dead-zone', edges } : null;
}
